import { t } from 'i18next';

import { AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { Stats } from '../utils/Stats';
import { ViewSettingsActions } from './ViewSettingsActions';
import { GlobalHelper } from '../utils/GlobalHelper';
import { SceneHelper } from '../utils/SceneHelper';
import { UIHelper } from '../utils/UIHelper';
import { CatalogStore } from '../stores/CatalogStore';

import cf = egr.wcf.cf;
import core = egr.wcf.core;
import utils = egr.wcf.utils;
import eaiws = egr.wcf.eaiws;

export abstract class CatalogActions {
    private static allowedItemTypes: Array<eaiws.catalog.CatalogItemType> = [
        'Folder',
        'Article',
        'Container',
        'Information'
    ];

    private static lookupOptions: eaiws.catalog.LookupOptions = ((): eaiws.catalog.LookupOptions => {
        const options: eaiws.catalog.LookupOptions = new eaiws.catalog.LookupOptions();
        options.itemTypes = CatalogActions.allowedItemTypes;
        options.displayMode = 'Planning3D';
        return options;
    })();

    public static isValidItem(item: eaiws.catalog.CatalogItem): boolean {
        if (CatalogActions.allowedItemTypes.indexOf(item.type) === -1) {
            return false;
        }

        if (item.type !== 'Information') {
            return true;
        }

        if (item.resources == null) {
            return false;
        }

        for (const resource of item.resources) {
            if (resource.value != null && resource.value.startsWith('http')) {
                return true;
            }
        }

        return false;
    }

    public static refreshCatalog(): void {
        const catalogPath: Array<eaiws.catalog.CatalogItem> = CatalogStore.getState().catalogPath;
        const currentItem: eaiws.catalog.CatalogItem = catalogPath[catalogPath.length - 1];
        if (currentItem != null) {
            // go back one step to update label of current opened path
            CatalogActions.leaveCatalogItem().then(() => {
                const oldItem: eaiws.catalog.CatalogItem = CatalogStore.getState().catalogItems.filter((catalogItem: eaiws.catalog.CatalogItem) => {
                    return (
                        currentItem.catalogId === catalogItem.catalogId &&
                        currentItem.catalogNodeKey === catalogItem.catalogNodeKey &&
                        currentItem.catalogPackageId === catalogItem.catalogPackageId
                    );
                })[0];
                // now go back to the previous opened path to update items
                CatalogActions.enterCatalogItem(oldItem != null ? oldItem : currentItem);
            });
        }
    }

    public static getCatalogPath(articleData: eaiws.basket.ArticleProperties): Promise<Array<string>> {
        return new Promise<Array<string>>((resolve, reject) => {
            const searchParameter: eaiws.catalog.SearchArticleParameterSet = new eaiws.catalog.SearchParameterSet();
            searchParameter.manufacturerId = articleData.manufacturerId;
            searchParameter.seriesId = articleData.seriesId;
            searchParameter.baseArticleNumber = articleData.baseArticleNumber;
            searchParameter.variantCode = articleData.variantCode;
            GlobalHelper.eaiwsSession.catalog.searchArticle(searchParameter).then((topCatalogItems) => {
                if (topCatalogItems != null && topCatalogItems.totalHits > 0) {
                    GlobalHelper.eaiwsSession.catalog.getCatalogPath(
                        topCatalogItems.scoredItems[0].item.catalogId,
                        topCatalogItems.scoredItems[0].item.catalogNodeKey
                    ).then((catalogPath) => {
                        if (catalogPath == null) {
                            reject();
                        } else {
                            const searchArray: Array<string> = [];
                            const promises: Array<Promise<eaiws.catalog.CatalogItem>> = [];
                            for (const catalogId of catalogPath) {
                                searchArray.push(catalogId);
                                promises.push(GlobalHelper.eaiwsSession.catalog.getCatalogItem(searchArray));
                            }
                            Promise.all(promises).then((catalogItems) => {
                                const result: Array<string> = [];
                                catalogItems.map((catalogItem) => {
                                    let label: string = catalogItem.label;
                                    if (!label) {
                                        label = catalogItem.name;
                                    }
                                    if (label) {
                                        result.push(label);
                                    }
                                });
                                resolve(result);
                            }).catch(() => {
                                reject();
                            });
                        }
                    });
                } else {
                    reject();
                }
            });
        });
    }

    public static async enterCatalogItem(item: eaiws.catalog.CatalogItem): Promise<void> {
        if (item == null) {
            return Promise.reject(new Error('Invalid catalog item'));
        }

        let catalogPath: Array<eaiws.catalog.CatalogItem> = [];
        if (
            item.type === 'Article' ||
            item.type === 'Container' ||
            utils.string.isNullOrEmpty(CatalogStore.getState().search)
        ) {
            catalogPath = CatalogStore.getState().catalogPath.concat(item);
        } else {
            const path: Array<string> = await GlobalHelper.eaiwsSession.catalog.getCatalogPath(
                item.catalogId,
                item.catalogNodeKey
            );

            for (let i: number = 0, l: number = path.length; i < l; i++) {
                catalogPath.push(await GlobalHelper.eaiwsSession.catalog.getCatalogItem(
                    path.slice(0, i + 1)
                ));
            }

        }

        return CatalogActions.updateCatalogPath(catalogPath);
    }

    public static enterPath(pPath: Array<eaiws.catalog.CatalogItem>): Promise<void> {
        return CatalogActions.updateCatalogPath(
            pPath
        );
    }

    public static leaveCatalogItem(): Promise<void> {
        return CatalogActions.updateCatalogPath(
            CatalogStore.getState().beforePath
        );
    }

    public static setFilter(value: string | null): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.FILTER_CATALOG,
            value: value
        });
    }

    public static searchCatalog(value: string | null): Promise<void> {
        if (!utils.string.isNullOrEmpty(value)) {
            return ViewSettingsActions.withSidebarLock<void>((): Promise<void> => {
                const catalogPath: Array<eaiws.catalog.CatalogItem> = CatalogStore.getState().catalogPath;
                if (catalogPath == null || catalogPath.length === 0) {
                    return Promise.resolve();
                }

                const parameterSet: eaiws.catalog.SearchParameterSet = new eaiws.catalog.SearchParameterSet();
                parameterSet.catalogIds = [catalogPath[0].catalogId];
                parameterSet.query = value;
                parameterSet.numberOfHits = 100;
                parameterSet.flags = ['FolderText'];
                const options: eaiws.catalog.LookupOptions = new eaiws.catalog.LookupOptions();
                options.itemTypes = [
                    'Article',
                    'Folder',
                    'Container',
                    'Information'
                ];

                return GlobalHelper.eaiwsSession.catalog.searchCatalogItems(
                    parameterSet,
                    options
                ).then(
                    (topCatalog: eaiws.catalog.TopCatalogItems | undefined) => {
                        AppDispatcher.dispatch({
                            actionType: ActionConstants.SEARCH_CATALOG,
                            value: {
                                items: (topCatalog != null && topCatalog.scoredItems != null) ?
                                    topCatalog.scoredItems.map(
                                        (scoredItem: eaiws.catalog.ScoredCatalogItem): eaiws.catalog.CatalogItem => {
                                            return scoredItem.item;
                                        }) :
                                    [],
                                catalogPath: [catalogPath[0]],
                                search: value
                            }
                        });
                    }
                );
            });
        }

        return Promise.resolve();
    }

    // FixMe: function does not update path, it only updates items in the path, e.g. label of path after language change is not altered
    private static updateCatalogPath(path: Array<eaiws.catalog.CatalogItem>): Promise<void> {
        const item: eaiws.catalog.CatalogItem = path[path.length - 1];

        if (item != null && item.type === 'Article') {
            // insert articles

            Stats.sendEvent({
                category: 'Article',
                action: 'insert',
                label: item.catalogId
            });

            const article: eaiws.catalog.ArticleCatalogItem = item as eaiws.catalog.ArticleCatalogItem;
            Stats.sendEvent({
                category: 'Article',
                action: 'insert - article number',
                label: article.baseArticleNumber
            });
            Stats.sendEvent({
                category: 'Article',
                action: 'insert - equipment + article number',
                label: path[path.length - 2].label + ' | ' + article.baseArticleNumber
            });

            return ViewSettingsActions.withLock<void>((): Promise<void> => {
                if (item instanceof eaiws.catalog.ArticleCatalogItem) {
                    return GlobalHelper.articleManager.insertArticle(item).then(
                        (element: core.mdl.SceneElement): Promise<void> => {
                            if (element == null) {
                                return Promise.reject(new Error('insertArticle resolved with null'));
                            }
                            return SceneHelper.insertElement(element);
                        }
                    ).then(() => {
                        ViewSettingsActions.articleAdded();
                    });
                }
                // unsupported item -> reject promise
                return Promise.reject(new Error('unsupported catalog article instance'));
            }).catch((error: Error) => {
                UIHelper.showMessage(t('The article could not be created.'));
            });
        }
        if (item != null && item.type === 'Container') {
            // insert container

            Stats.sendEvent({
                category: 'Article',
                action: 'insert',
                label: item.catalogId
            });

            return ViewSettingsActions.withLock<void>((): Promise<void> => {
                const tPecImport: cf.io.PecImport = new cf.io.PecImport(GlobalHelper.articleManager);
                return tPecImport.importFromCatalog(item).then((pElements: Array<core.mdl.SceneElement>) =>    {
                    return SceneHelper.insertElements(pElements);
                }).then(() => {
                    ViewSettingsActions.articleAdded();
                });
            }).catch((error: Error) => {
                UIHelper.showMessage(t('The article could not be created.'));
            });
        }

        if (item != null && item.type === 'Information' && item.resources != null) {
            for (const resource of item.resources) {
                if (resource.value != null && resource.value.startsWith('http')) {
                    const a: HTMLAnchorElement = document.createElement('a');
                    a.href = resource.value;
                    a.target = '_blank';
                    a.click();
                    return Promise.resolve();
                }
            }
        }

        // list subitems
        return ViewSettingsActions.withSidebarLock<void>((): Promise<void> => {
            return GlobalHelper.eaiwsSession.catalog.listCatalogItems(
                path.map(
                    (mapItem: eaiws.catalog.CatalogItem): string => {
                        return mapItem.name;
                }),
                CatalogActions.lookupOptions
            ).then(
                (items: Array<eaiws.catalog.CatalogItem>) => {
                    AppDispatcher.dispatch({
                        actionType: ActionConstants.CATALOG_UPDATE,
                        value: {
                            items: items,
                            catalogPath: path
                        }
                    });
                }
            );
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/CatalogActions.ts